import { Button, Dialog, Devices, Facility, isValidLaneWindowHours, MAX_LANE_WINDOW_HOURS } from "@app/shared";
import cn from "classnames";
import React, { useEffect, useState } from "react";
import { toastSuccess } from "../../../helpers";
import { hasLaneCollection } from "../../../helpers/versionCompare";
import { NumericInput, Select } from "../inputs";

type CollectAndGenerateDialogProps = {
    open: boolean;
    facility: Facility;
    onClose: () => void;
    onReview: (deviceId: string, fromDate: Date) => void;
    onChanged?: () => void;
};

const DURATION_OPTIONS = [1, 2, 3];

export const CollectAndGenerateDialog: React.FC<CollectAndGenerateDialogProps> = ({ open, facility, onClose, onReview, onChanged }) => {
    const eligible = (facility.devices ?? []).filter((d) => hasLaneCollection(d.firmwareVersion));
    const [deviceId, setDeviceId] = useState<string | undefined>(eligible[0]?.id);
    const [collectHours, setCollectHours] = useState<number | undefined>(72);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        if (!open) return;
        setDeviceId(eligible[0]?.id);
        setCollectHours(72);
        setLoading(false);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open]);

    const device = eligible.find((d) => d.id === deviceId);
    const until = device?.laneCollectionUntil;
    const startedAt = device?.laneCollectionStartedAt;
    const active = !!until;
    const ready = active && until! <= new Date();

    const arm = async () => {
        if (!deviceId) return;
        setLoading(true);
        try {
            const untilDate = new Date(Date.now() + (collectHours ?? 72) * 60 * 60 * 1000);
            await Devices.armLaneCollection(deviceId, untilDate);
            toastSuccess(`Collecting fresh traffic — check back after ${untilDate.toLocaleDateString()}.`);
            onChanged?.();
            onClose();
        } finally {
            setLoading(false);
        }
    };

    const cancel = async () => {
        if (!deviceId) return;
        setLoading(true);
        try {
            await Devices.cancelLaneCollection(deviceId);
            toastSuccess("Collection canceled.");
            onChanged?.();
            onClose();
        } finally {
            setLoading(false);
        }
    };

    const handleOk = () => {
        if (loading || !deviceId) return;
        if (active) {
            onReview(deviceId, startedAt ?? new Date(Date.now() - (collectHours ?? 72) * 60 * 60 * 1000));
            onClose();
            return;
        }
        void arm();
    };

    const okText = loading ? "Working…" : active ? "Review suggestions" : "Start Collecting";
    const hoursValid = isValidLaneWindowHours(collectHours);

    return (
        <Dialog
            show={open}
            title="Collect & Generate Lane Lines"
            onOkClick={handleOk}
            onCancelClick={onClose}
            okButtonText={okText}
            disableOkButton={loading || !deviceId || (!active && !hoursValid)}
            showCancelButton={true}
        >
            <div className="flex flex-col gap-4 w-full">
                {eligible.length === 0 ? (
                    <p className="text-sm text-gray-600">
                        No devices on firmware <strong>2.0.9.0</strong> or newer. Update the device firmware to collect fresh traffic for lane generation.
                    </p>
                ) : (
                    <>
                        <p className="text-sm text-gray-600">
                            Have the device collect <strong>fresh</strong> traffic starting now, then generate lane lines from only that window — ignoring
                            older or bad historical data. Review and save the suggestions when it's ready.
                        </p>

                        {eligible.length > 1 && (
                            <Select
                                label="Device"
                                value={deviceId}
                                onChange={(e) => setDeviceId(e.target.value)}
                                options={eligible.map((d) => ({ label: d.name ?? "Device", value: d.id ?? "" }))}
                            />
                        )}

                        {active ? (
                            <div className="flex flex-col gap-3">
                                <p className="text-sm">
                                    {ready ? (
                                        <>
                                            Collection window finished on <strong>{until!.toLocaleString()}</strong>. Review the suggested lane lines.
                                        </>
                                    ) : (
                                        <>
                                            Collecting fresh traffic until <strong>{until!.toLocaleString()}</strong>. You can review early, or come back when it's
                                            done.
                                        </>
                                    )}
                                </p>
                                <div>
                                    <Button className="btn-outline-danger" onClick={cancel} disabled={loading}>
                                        Cancel collection
                                    </Button>
                                </div>
                            </div>
                        ) : (
                            <div className="flex flex-col gap-1">
                                <span className="text-sm font-medium">Collect for up to</span>
                                <div className="flex gap-2">
                                    {DURATION_OPTIONS.map((d) => (
                                        <Button key={d} className={cn("flex-1", { "border-brand-secondary border-2": collectHours === d * 24 })} onClick={() => setCollectHours(d * 24)}>
                                            {d} {d === 1 ? "day" : "days"}
                                        </Button>
                                    ))}
                                </div>
                                <div className="mt-1 flex items-center gap-2">
                                    <NumericInput value={collectHours} allowFloat={false} onChange={(v) => setCollectHours(v)} placeholder="Custom" className="w-24" />
                                    <span className="text-xs text-gray-500">hours (1–{MAX_LANE_WINDOW_HOURS}, up to 10 days)</span>
                                </div>
                                {!hoursValid && <span className="text-xs text-red-600">Enter between 1 and {MAX_LANE_WINDOW_HOURS} hours.</span>}
                                <span className="text-xs text-gray-500">
                                    It generates as soon as there's enough traffic (often within a day at a busy intersection) — the window is just the maximum.
                                </span>
                            </div>
                        )}
                    </>
                )}
            </div>
        </Dialog>
    );
};
